import {
  Circle,
  GoogleMap,
  InfoWindow,
  Marker,
  Polygon,
} from '@react-google-maps/api';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import uuidv4 from 'uuid/v4';

const Map = (props) => {
  let {
    center,
    zoom,
    markers,
    mapType,
    circles,
    polygons,

    selectPositionMode,
    setSelectedPosition,

    drawCircleMode,
    setCircleRadius,

    drawAreaMode,
    addAreaVertex,
    updateActiveAreaPositions,
    onDrawnAreaClick,
    deleteAreaVertex,
  } = props;
  const [activeMarker, setActiveMarker] = useState(null);
  const [mapRef, setMapRef] = useState(null);

  const [editableCircle, setEditableCircle] = useState(null);
  const [editablePolygon, setEditablePolygon] = useState(null);

  const [mapCenter, setMapCenter] = useState({
    lat: 60.16955736341621,
    lng: 24.93818796706058,
  });

  const centerOptions = useMemo(
    () => ({ lat: mapCenter?.lat, lng: mapCenter?.lng }),
    [mapCenter]
  );

  const [mapZoom, setMapZoom] = useState(zoom);
  const [infoOpen, setInfoOpen] = useState(false);
  const [markerMap, setMarkerMap] = useState({});

  const handleActiveMarker = (marker) => {
    if (marker === activeMarker) {
      return;
    }
    setActiveMarker(marker);
    setMapCenter(marker.position);
    if (mapZoom < 13) {
      setMapZoom(13);
    }
    if (infoOpen) {
      setInfoOpen(false);
    }
    setInfoOpen(true);
  };

  // Called when infobox is closed, closes info and sets active markers as null
  const handleCloseInfo = () => {
    setActiveMarker(null);
    setInfoOpen(false);
  };

  // Centers and zooms the map so that all markers fit inside
  const fitBounds = async (map) => {
    if (!map) return;
    if (markers.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.map((place) => {
        bounds.extend(place.position);
        return place.id;
      });
      map.fitBounds(bounds);
    } else if (markers.length === 1) {
      setMapCenter(markers[0].position);
      setMapZoom(15);
    } else {
      setMapCenter(center);
      setMapZoom(12);
    }
  };

  const circleLoadHandler = (circle) => {
    return setEditableCircle(circle);
  };

  const polygonLoadHandler = (polygon) => {
    return setEditablePolygon(polygon);
  };

  const markerLoadHandler = (marker, place) => {
    return setMarkerMap((prevState) => {
      return { ...prevState, [place.id]: marker };
    });
  };

  const loadHandler = useCallback(
    (map) => {
      setMapRef(map);
      fitBounds(map);
    },
    [markers]
  );

  const handleMapClick = useCallback(
    (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      const newPosition = { id: uuidv4(), latitude: lat, longitude: lng };
      // setSelectedPosition(newPosition);

      console.log('Map.js:', newPosition);

      if (setSelectedPosition && selectPositionMode)
        setSelectedPosition(newPosition);
      if (addAreaVertex && drawAreaMode) {
        addAreaVertex({ latitude: lat, longitude: lng });
      }
    },
    [selectPositionMode, setSelectedPosition, drawAreaMode, addAreaVertex]
  );

  const handleMarkerDragEnd = useCallback(
    (event, marker) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      const newPosition = { id: marker.id, latitude: lat, longitude: lng };

      if (setSelectedPosition && selectPositionMode)
        setSelectedPosition(newPosition);
    },
    [selectPositionMode, setSelectedPosition]
  );
  const handleCircleRadiusChanged = useCallback(() => {
    if (!editableCircle) return;
    const newRadius = editableCircle.getRadius();
    console.log('New radius:', newRadius);
    setCircleRadius && setCircleRadius(newRadius);
  }, [editableCircle, setCircleRadius]);

  const handleCircleCenterChanged = useCallback(() => {
    if (!editableCircle) return;
    const center = editableCircle.getCenter();
    const newPosition = {
      latitude: center.lat(),
      longitude: center.lng(),
    };

    // Prevent loop: only update if different
    if (typeof setSelectedPosition === 'function') {
      setSelectedPosition((prev) => {
        if (
          !prev ||
          prev.latitude !== newPosition.latitude ||
          prev.longitude !== newPosition.longitude
        ) {
          console.log('New circle center:', newPosition);
          return newPosition;
        }
        return prev;
      });
    }
  }, [editableCircle, setSelectedPosition]);

  const handlePolygonDblClick = useCallback(
    (event) => {
      // event.vertex is set only when the double-click lands on a vertex
      // handle of the editable polygon
      if (event.vertex === undefined || event.vertex === null) return;
      if (event.stop) event.stop();
      if (deleteAreaVertex && drawAreaMode) {
        deleteAreaVertex(event.vertex);
      }
    },
    [deleteAreaVertex, drawAreaMode]
  );

  const handlePolygonPathsChanged = useCallback(() => {
    if (!editablePolygon) return;

    const pathArray = editablePolygon
      .getPath()
      .getArray()
      .map((latLng) => ({
        latitude: latLng.lat(),
        longitude: latLng.lng(),
      }));

    console.log('Updated polygon path:', pathArray);

    if (updateActiveAreaPositions && drawAreaMode) {
      updateActiveAreaPositions(pathArray);
    }
  }, [editablePolygon, updateActiveAreaPositions, drawAreaMode]);

  return (
    <GoogleMap
      onLoad={loadHandler}
      onZoomChanged={() => setMapZoom(mapRef.getZoom())}
      zoom={mapZoom}
      center={centerOptions}
      mapContainerStyle={{
        height: '70vh',
        width: '100%',
      }}
      mapTypeId={mapType}
      onClick={(e) => handleMapClick(e)}
      options={{
        draggableCursor:
          drawAreaMode || selectPositionMode ? 'crosshair' : 'grab',
        draggingCursor: 'grabbing',
        clickableIcons: false,
      }}
    >
      {markers &&
        markers.map((marker) => {
          return (
            <Marker
              key={marker.id}
              position={marker.position}
              options={marker.options}
              icon={{
                path: marker.icon.path,
                fillColor: marker.icon.color || '#FF0000',
                fillOpacity: marker.icon.fillOpacity || 0.8,
                strokeColor: '#000',
                strokeWeight: 1,
                strokeOpacity: marker.icon.strokeOpacity || 1,
                scale: marker.icon.scale || 1.5,
                anchor: new window.google.maps.Point(12, 24),
              }}
              draggable={marker.draggable}
              onDragEnd={(e) =>
                marker.draggable ? handleMarkerDragEnd(e, marker) : null
              }
              onClick={() => {
                if (marker.desc || marker.address) {
                  handleActiveMarker(marker);
                }
              }}
              onLoad={(eventMarker) => markerLoadHandler(eventMarker, marker)}
            />
          );
        })}
      {infoOpen && activeMarker && (
        <InfoWindow
          anchor={markerMap[activeMarker.id]}
          onCloseClick={() => handleCloseInfo()}
        >
          <div style={{ color: 'black' }}>
            {activeMarker.desc}
            <br />
            {activeMarker.address}
          </div>
        </InfoWindow>
      )}
      {circles &&
        circles.map((circle) => {
          const editable = drawCircleMode && circle.id === 'editable-circle';

          return (
            <Circle
              key={circle.id}
              onLoad={editable ? circleLoadHandler : undefined}
              center={circle.center}
              options={circle.options}
              onRadiusChanged={handleCircleRadiusChanged}
              onCenterChanged={handleCircleCenterChanged}
            />
          );
        })}
      {polygons &&
        polygons.map((polygon) => {
          const editable = drawAreaMode && polygon.id === 'editable-polygon';
          return (
            <Polygon
              key={polygon.id}
              onLoad={editable ? polygonLoadHandler : undefined}
              paths={polygon.paths}
              options={polygon.options}
              onMouseUp={editable ? handlePolygonPathsChanged : undefined}
              onDblClick={editable ? handlePolygonDblClick : undefined}
              onClick={
                !editable && polygon.areaId && onDrawnAreaClick
                  ? (event) =>
                      onDrawnAreaClick(polygon.areaId, {
                        latitude: event.latLng.lat(),
                        longitude: event.latLng.lng(),
                      })
                  : undefined
              }
            />
          );
        })}
    </GoogleMap>
  );
};
const memoMap = memo(Map);
export default memoMap;
